import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
  TipsAndUpdates as TipsAndUpdatesIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { designTokens } from '../../theme/designTokens';
import InlineAnalysisBadge from './InlineAnalysisBadge';
import { getFieldAnalyses } from './analysisFieldMapper';
import { bg, text as textColors, border, shadow } from '../../utils/darkModeAdapter';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: '100%',
  margin: '0 auto',
  borderRadius: 12,
  backgroundColor: bg(theme).card,
  boxShadow: shadow(theme).sm,
  border: `1px solid ${border(theme).default}`,
  overflow: 'hidden',
  position: 'relative',
  transition: designTokens.transitions.normal,
  '&:hover': {
    boxShadow: shadow(theme).md,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: 8,
  }
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  paddingLeft: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
    marginBottom: theme.spacing(1.5),
  }
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 8,
  overflow: 'visible',
  backgroundColor: bg(theme).subtle,
  border: `1px solid ${border(theme).light}`,
  boxShadow: 'none',
  position: 'relative',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: shadow(theme).sm,
    backgroundColor: bg(theme).card,
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: '1rem',
  marginBottom: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
  }
}));

const ContentPreview = styled(Box)(({ theme }) => ({
  color: textColors(theme).primary,
  fontSize: '0.95rem',
  lineHeight: 1.6,
  marginTop: theme.spacing(1),
  whiteSpace: 'pre-wrap',
  '& p': {
    margin: '0.5em 0',
  }
}));

/**
 * 求人票セクション（各項目ごとの編集可能なカード）
 */
// セクションタイトル → fieldMapping キーの対応
const SECTION_TO_FIELD = {
  'タイトル': 'title',
  '企業のポイント': 'companyPoints',
  '概要': 'summary',
  '仕事内容': 'jobDescription',
  '求める人材': 'targetPerson',
  '待遇・福利厚生': 'benefits',
  '選考プロセス': 'applicationProcess',
};

const JobSection = ({ title, content, onChange, index, analysis, aiAnalysis }) => {
  const fieldName = SECTION_TO_FIELD[title];
  const fieldAnalyses = analysis ? getFieldAnalyses(analysis, fieldName) : [];

  // AI分析結果をaxis key → { assessment, suggestions } のマップに変換
  const aiMap = {};
  if (aiAnalysis?.items) {
    aiAnalysis.items
      .filter(item => item.fieldRelevance && item.fieldRelevance.includes(fieldName))
      .forEach(item => {
        aiMap[item.axis] = { assessment: item.assessment, suggestions: item.suggestions };
      });
  }

  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);
  
  // セクションの内容が変わったら、編集用のテキストも更新
  useEffect(() => {
    setEditedContent(content);
  }, [content]);
  
  const handleEdit = () => {
    setIsEditing(true);
  };
  
  const handleSave = () => {
    onChange(index, editedContent);
    setIsEditing(false);
  };
  
  const handleCancel = () => {
    setEditedContent(content);
    setIsEditing(false);
  };

  // テキストの表示（全セクションで改行を保持）
  const renderContent = () => {
    if (!content) {
      return <Typography color="text.secondary" fontStyle="italic">内容がありません</Typography>;
    }

    return (
      <ContentPreview>
        {content.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {line}
            {i < content.split('\n').length - 1 && <br />}
          </React.Fragment>
        ))}
      </ContentPreview>
    );
  };

  return (
    <SectionCard>
      <CardContent sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <SectionTitle>{title}</SectionTitle>
          <Box>
            {isEditing ? (
              <>
                <IconButton size="small" onClick={handleSave} color="success" sx={{ mr: 0.5 }}>
                  <SaveIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleCancel} color="default">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <IconButton size="small" onClick={handleEdit} color="primary">
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {isEditing ? (
          <TextField
            fullWidth
            multiline
            rows={title === '仕事内容' ? 6 : 4}
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.9rem',
                lineHeight: 1.5,
              }
            }}
          />
        ) : (
          renderContent()
        )}

        {/* インライン分析バッジ（AI補足を統合） */}
        {fieldAnalyses.length > 0 && (
          <InlineAnalysisBadge analyses={fieldAnalyses} aiMap={Object.keys(aiMap).length > 0 ? aiMap : undefined} />
        )}
      </CardContent>
    </SectionCard>
  );
};

/**
 * 生成された求人票カード - 新フォーマット
 */
function JobPostCard({ jobPostMd, setJobPostMd, onRequestQualityCheck, isCheckingQuality, analysis, onRequestAiQualityCheck, isAiChecking, aiAnalysis }) {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  
  // 求人票の各セクション
  const [sections, setSections] = useState([
    { title: 'タイトル', content: '' },
    { title: '企業のポイント', content: '' },
    { title: '概要', content: '' },
    { title: '仕事内容', content: '' },
    { title: '求める人材', content: '' },
    { title: '待遇・福利厚生', content: '' },
    { title: '選考プロセス', content: '' }
  ]);

  // セクション見出しとインデックスの対応
  const SECTION_HEADERS = [
    { regex: /^## 企業のポイント/, index: 1 },
    { regex: /^## (概要|企業情報)/, index: 2 },
    { regex: /^## (仕事内容|業務内容)/, index: 3 },
    { regex: /^## (求める人材|求める人物像)/, index: 4 },
    { regex: /^## (待遇・福利厚生|待遇|福利厚生)/, index: 5 },
    { regex: /^## (選考プロセス|選考フロー|応募方法)/, index: 6 },
  ];

  // マークダウンから各セクションへの変換
  useEffect(() => {
    if (!jobPostMd) return;

    try {
      const lines = jobPostMd.split('\n');
      let currentSection = -1;
      let newSections = [
        { title: 'タイトル', content: '' },
        { title: '企業のポイント', content: '' },
        { title: '概要', content: '' },
        { title: '仕事内容', content: '' },
        { title: '求める人材', content: '' },
        { title: '待遇・福利厚生', content: '' },
        { title: '選考プロセス', content: '' }
      ];

      // タイトルを抽出（最初の# で始まる行）
      const titleIndex = lines.findIndex(line => line.startsWith('# '));
      if (titleIndex >= 0) {
        newSections[0].content = lines[titleIndex].replace('# ', '');
      }

      // 残りのセクションを行ごとに処理
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // セクション見出しの判定
        const matchedHeader = SECTION_HEADERS.find(h => h.regex.test(line));
        if (matchedHeader) {
          currentSection = matchedHeader.index;
          continue;
        }
        // 未知の##見出しはスキップ
        if (line.startsWith('##')) {
          currentSection = -1;
          continue;
        }

        // 現在のセクションに内容を追加（マークダウン記法をそのまま保持）
        if (currentSection >= 0 && !line.startsWith('#')) {
          // 空行も含めて保持（構造を維持するため）
          if (newSections[currentSection].content) {
            newSections[currentSection].content += '\n' + line;
          } else if (line.trim() !== '') {
            newSections[currentSection].content = line;
          }
        }
      }

      // 末尾の空行をトリム
      newSections = newSections.map(section => ({
        ...section,
        content: section.content.replace(/\n+$/, '')
      }));

      setSections(newSections);
    } catch (error) {
      console.error('Error parsing markdown:', error);
    }
  }, [jobPostMd]);

  // 各セクションの更新
  const handleSectionChange = (index, content) => {
    const newSections = [...sections];
    newSections[index].content = content;
    setSections(newSections);

    // マークダウンを再構築して更新
    const updatedMarkdown = updateMarkdown(newSections);
    setJobPostMd(updatedMarkdown);
  };

  // セクションからマークダウンへの変換（全セクションを再構築）
  const updateMarkdown = (updatedSections) => {
    let markdown = `# ${updatedSections[0].content}\n\n`;

    const sectionHeaders = [
      '企業のポイント',
      '概要',
      '仕事内容',
      '求める人材',
      '待遇・福利厚生',
      '選考プロセス',
    ];

    for (let i = 1; i < updatedSections.length; i++) {
      if (updatedSections[i].content.trim()) {
        markdown += `## ${sectionHeaders[i - 1]}\n\n${updatedSections[i].content}\n\n`;
      }
    }

    return markdown;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(jobPostMd).then(() => {
      setSnackbarOpen(true);
    });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <StyledPaper sx={{ maxHeight: '100%', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <CardTitle variant="h6">
          生成された求人票
        </CardTitle>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {onRequestAiQualityCheck && (
            <Tooltip title="AIが文脈・トーン・論理性など、通常チェックでは補えない観点からより詳しく品質を分析します" arrow>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon sx={{ fontSize: '1rem' }} />}
                  onClick={onRequestAiQualityCheck}
                  disabled={isAiChecking}
                  size={isSmDown ? 'small' : 'medium'}
                  sx={{
                    borderRadius: 9999,
                    textTransform: 'none',
                    py: isSmDown ? 0.5 : 0.75,
                    px: isSmDown ? 1.5 : 2,
                    fontSize: isSmDown ? '0.75rem' : '0.85rem',
                    color: '#64B5F6',
                    borderColor: '#90CAF9',
                    '&:hover': {
                      borderColor: '#64B5F6',
                      backgroundColor: 'rgba(100, 181, 246, 0.06)',
                    },
                    '&.Mui-disabled': {
                      color: '#B3D9F7',
                      borderColor: '#BBDEFB',
                    },
                  }}
                >
                  {isAiChecking ? 'AI分析中...' : 'AI品質チェック'}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            size={isSmDown ? 'small' : 'medium'}
            sx={{
              borderRadius: 9999,
              textTransform: 'none',
              boxShadow: 1,
              py: isSmDown ? 0.5 : 0.75,
              px: isSmDown ? 1.5 : 2,
              fontSize: isSmDown ? '0.75rem' : '0.85rem',
            }}
          >
            全体をコピー
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          p: 2.5,
          mb: 3,
          bgcolor: 'rgba(76, 175, 80, 0.12)',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <TipsAndUpdatesIcon sx={{ mr: 1, color: '#2e7d32' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2e7d32' }}>
            さらに完成度を上げるには？
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          この求人内容をさらに良くしたい場合は、<strong>チャット機能</strong>を使って簡単に修正・改善できます。必要な情報を追加したり、文章をより魅力的にするアドバイスを求めてみましょう。求人内容をコピーしてチャットに貼り付けることもできるので、気になる部分を選んで相談してみてください。
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        {sections.map((section, index) => {
          // 内容が空のセクションは非表示（タイトルは常に表示）
          if (index > 0 && !section.content.trim()) return null;
          return (
            <JobSection
              key={index}
              title={section.title}
              content={section.content}
              onChange={handleSectionChange}
              index={index}
              analysis={analysis}
              aiAnalysis={aiAnalysis}
            />
          );
        })}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          コピーしました！
        </Alert>
      </Snackbar>
    </StyledPaper>
  );
}

export default JobPostCard; 